import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaCloudRain, FaTree, FaWater, FaFire, FaDove, FaWind, FaMoon, FaTint, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "Relaxing Sounds",
    description: "Rahatlatıcı sesler dinleyin. İstediğiniz sesleri harmanlayın ve seviyelerini ayarlayın. Huzur bulun.",
    image_url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80"
  }
};

const sounds = [
  { id: 1, name: "Yağmur", icon: FaCloudRain, color: "from-blue-400 to-blue-600", defaultVolume: 50, audioFile: "rain.mp3" },
  { id: 2, name: "Orman", icon: FaTree, color: "from-green-400 to-green-600", defaultVolume: 50, audioFile: "forest.mp3" },
  { id: 3, name: "Okyanus", icon: FaWater, color: "from-cyan-400 to-cyan-600", defaultVolume: 50, audioFile: "ocean.mp3" },
  { id: 4, name: "Şömine", icon: FaFire, color: "from-orange-400 to-red-600", defaultVolume: 50, audioFile: "fireplace.mp3" },
  { id: 5, name: "Kuşlar", icon: FaDove, color: "from-yellow-400 to-yellow-600", defaultVolume: 50, audioFile: "birds.mp3" },
  { id: 6, name: "Rüzgar", icon: FaWind, color: "from-gray-400 to-gray-600", defaultVolume: 50, audioFile: "wind.mp3" },
  { id: 7, name: "Gece", icon: FaMoon, color: "from-indigo-400 to-purple-600", defaultVolume: 50, audioFile: "night.mp3" },
  { id: 8, name: "Şelale", icon: FaTint, color: "from-teal-400 to-teal-600", defaultVolume: 50, audioFile: "waterfall.mp3" }
];

export default function RelaxingSounds({ id, moduleId, isOwner }) {
  const [activeSounds, setActiveSounds] = useState({});
  const [volumes, setVolumes] = useState({});
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const audioRefs = useRef({});

  useEffect(() => {
    // Initialize volumes
    const initialVolumes = {};
    sounds.forEach(sound => {
      initialVolumes[sound.id] = sound.defaultVolume;
    });
    setVolumes(initialVolumes);

    // Cleanup on unmount
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, []);

  // Lazy load audio when needed
  const getOrCreateAudio = useCallback((soundId) => {
    if (!audioRefs.current[soundId]) {
      const sound = sounds.find(s => s.id === soundId);
      if (!sound) return null;

      // Try different possible paths
      const basePath = process.env.PUBLIC_URL || '';
      const audioPath = `${basePath}/assets/sounds/${sound.audioFile}`;
      const audio = new Audio(audioPath);
      audio.loop = true;
      audio.volume = sound.defaultVolume / 100;
      audio.preload = 'auto';
      
      // Error handling
      audio.addEventListener('error', (e) => {
        console.error(`Ses dosyası yüklenemedi (${sound.name}):`, {
          path: audioPath,
          error: e,
          readyState: audio.readyState,
          networkState: audio.networkState,
          src: audio.src
        });
      });
      
      // Log when audio is loaded successfully
      audio.addEventListener('loadeddata', () => {
        console.log(`Ses yüklendi: ${sound.name}`);
      });
      
      audioRefs.current[soundId] = audio;
    }
    return audioRefs.current[soundId];
  }, []);

  // Update volume when volumes state changes
  useEffect(() => {
    Object.keys(volumes).forEach(soundId => {
      const audio = audioRefs.current[soundId];
      if (audio) {
        audio.volume = volumes[soundId] / 100;
      }
    });
  }, [volumes]);

  // Handle play/pause when activeSounds changes
  useEffect(() => {
    Object.keys(activeSounds).forEach(soundId => {
      if (activeSounds[soundId]) {
        // Lazy load audio when activated
        const audio = getOrCreateAudio(parseInt(soundId));
        if (audio) {
          // Update volume from state
          const currentVolume = volumes[parseInt(soundId)];
          if (currentVolume !== undefined) {
            audio.volume = currentVolume / 100;
          }
          
          // Try to play
          const playAudio = () => {
            audio.play().catch(err => {
              console.error(`Ses çalınamadı (ID: ${soundId}):`, err);
            });
          };

          // If audio is ready, play immediately
          if (audio.readyState >= 2) {
            playAudio();
          } else {
            // Wait for audio to be ready
            const onCanPlay = () => {
              playAudio();
            };
            audio.addEventListener('canplaythrough', onCanPlay, { once: true });
            
            // Load the audio if not loaded
            if (audio.readyState === 0) {
              audio.load();
            }
          }
        }
      } else {
        // Pause audio
        const audio = audioRefs.current[soundId];
        if (audio) {
          audio.pause();
        }
      }
    });
  }, [activeSounds, volumes, getOrCreateAudio]);

  const handleToggleSound = (soundId) => {
    setActiveSounds(prev => ({
      ...prev,
      [soundId]: !prev[soundId]
    }));
  };

  const handleVolumeChange = (soundId, volume) => {
    setVolumes(prev => ({
      ...prev,
      [soundId]: volume
    }));
  };

  const handleStopAll = () => {
    setActiveSounds({});
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) {
        audio.pause();
      }
    });
  };

  const activeCount = Object.values(activeSounds).filter(Boolean).length;

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 py-8 pb-24 flex-1">
          {/* Info Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
            <p className="text-xs text-gray-500 text-center leading-relaxed">
              İstediğiniz sesleri seçin ve harmanlayın. Ses seviyelerini ayarlayarak istediğiniz atmosferi yaratın.
            </p>
          </div>

          {/* Active Sounds Counter */}
          {activeCount > 0 && (
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {activeCount} ses aktif
              </span>
              <button
                onClick={handleStopAll}
                className="text-sm text-red-600 hover:text-red-700 font-semibold"
              >
                Tümünü Durdur
              </button>
            </div>
          )}

          {/* Sounds Grid */}
          <div className="space-y-4 mb-6">
            {sounds.map((sound) => {
              const isActive = activeSounds[sound.id];
              const volume = volumes[sound.id] || 50;

              return (
                <div
                  key={sound.id}
                  className={`bg-white border-2 rounded-xl p-4 transition-all duration-200 ${
                    isActive 
                      ? 'border-[#3DA7B5] shadow-lg scale-[1.02]' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => handleToggleSound(sound.id)}
                      className={`flex items-center gap-3 flex-1 text-left ${
                        isActive ? 'opacity-100' : 'opacity-60'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${sound.color} flex items-center justify-center shadow-md`}>
                        {(() => {
                          const IconComponent = sound.icon;
                          return <IconComponent className="text-2xl text-white" />;
                        })()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{sound.name}</h3>
                        <p className="text-xs text-gray-500">
                          {isActive ? 'Çalıyor' : 'Duraklatıldı'}
                        </p>
                      </div>
                    </button>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isActive ? <FaVolumeUp className="text-xl" /> : <FaVolumeMute className="text-xl" />}
                    </div>
                  </div>

                  {isActive && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-3">
                        <FaVolumeMute className="text-xs text-gray-500 w-8" />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={volume}
                          onChange={(e) => handleVolumeChange(sound.id, parseInt(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3DA7B5]"
                        />
                        <FaVolumeUp className="text-xs text-gray-500 w-8" />
                        <span className="text-xs font-semibold text-gray-700 w-10 text-right">
                          {volume}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tips */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h4 className="text-xs font-medium text-gray-400 mb-3">İpuçları</h4>
            <ul className="text-xs text-gray-500 space-y-1.5 leading-relaxed">
              <li>• Birden fazla sesi harmanlayarak kendi atmosferinizi yaratın</li>
              <li>• Ses seviyelerini ayarlayarak istediğiniz dengeyi bulun</li>
              <li>• Meditasyon veya uyku için kullanabilirsiniz</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

